import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import Stepper from './components/Stepper';
import StepCompanyIdentity from './components/StepCompanyIdentity';
import StepLegalProfile from './components/StepLegalProfile';
import StepPrimaryContact from './components/StepPrimaryContact';
import StepBankAccount from './components/StepBankAccount';
import StepRoles from './components/StepRoles';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

interface OnboardingData {
  company: Record<string, string>;
  legal: Record<string, string>;
  contact: Record<string, string>;
  bank: Record<string, string>;
  roles: string[];
}

const initialData: OnboardingData = {
  company: {},
  legal: {},
  contact: {},
  bank: {},
  roles: [],
};

function validateStep(step: number, data: OnboardingData): Record<string, string> {
  const errors: Record<string, string> = {};
  switch (step) {
    case 1:
      if (!data.company.name?.trim()) errors.name = 'Company name is required';
      if (!data.company.shortName?.trim()) errors.shortName = 'Short name is required';
      if (!data.company.edrpou?.trim()) errors.edrpou = 'EDRPOU is required';
      if (!data.company.country?.trim()) errors.country = 'Country is required';
      break;
    case 2:
      if (!data.legal.fullLegalName?.trim()) errors.fullLegalName = 'Full legal name is required';
      if (!data.legal.legalAddress?.trim()) errors.legalAddress = 'Legal address is required';
      if (!data.legal.registrationCountry?.trim()) errors.registrationCountry = 'Registration country is required';
      if (!data.legal.vatStatus?.trim()) errors.vatStatus = 'VAT status is required';
      if (!data.legal.directorName?.trim()) errors.directorName = 'Director name is required';
      break;
    case 3:
      if (!data.contact.fullName?.trim()) errors.fullName = 'Full name is required';
      if (!data.contact.position?.trim()) errors.position = 'Position is required';
      if (!data.contact.email?.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contact.email)) {
        errors.email = 'Invalid email format';
      }
      if (!data.contact.phone?.trim()) errors.phone = 'Phone is required';
      if (!data.contact.contactType?.trim()) errors.contactType = 'Contact type is required';
      break;
    case 4:
      if (!data.bank.bankName?.trim()) errors.bankName = 'Bank name is required';
      if (!data.bank.iban?.trim()) errors.iban = 'IBAN is required';
      if (!data.bank.currency?.trim()) errors.currency = 'Currency is required';
      if (!data.bank.mfo?.trim()) errors.mfo = 'MFO / SWIFT is required';
      if (!data.bank.accountHolder?.trim()) errors.accountHolder = 'Account holder is required';
      break;
    case 5:
      if (data.roles.length === 0) errors.roles = 'Select at least one role';
      break;
  }
  return errors;
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { isDark } = useTheme();

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const separatorBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const backBtnBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const backBtnColor = isDark ? '#E0E0E0' : '#1A1A1A';
  const successBg = isDark ? '#1A1A1A' : '#fff';
  const successBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const successIconBg = isDark ? 'rgba(13,122,60,0.15)' : 'rgba(13,122,60,0.1)';
  const outlineBtnBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const outlineBtnColor = isDark ? '#E0E0E0' : '#1A1A1A';

  const STEP_LABELS = [
    t('ob_step_company'),
    t('ob_step_legal'),
    t('ob_step_contact'),
    t('ob_step_bank'),
    t('ob_step_roles'),
  ];
  const [step, setStep] = useState(1);
  const [data, setData] = useState<OnboardingData>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);

  const updateStepData = useCallback((key: keyof OnboardingData, value: unknown) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setErrors({});
  }, []);

  const handleNext = () => {
    const validationErrors = validateStep(step, data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      addToast(t('ob_fill_required'), 'warning');
      return;
    }
    if (step < 5) {
      setStep((s) => s + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((s) => s - 1);
      setErrors({});
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateStep(5, data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      addToast(t('ob_select_role'), 'warning');
      return;
    }
    setSubmitting(true);
    try {
      const vatStatusRaw = (data.legal.vatStatus ?? '').toLowerCase().trim();
      const vatStatusBool = ['true', 'registered', 'yes', '1'].includes(vatStatusRaw);
      const contactTypeMap: Record<string, string> = {
        technical: 'technical', technician: 'technical', legal: 'legal', financial: 'financial',
        dispatch: 'dispatch', commercial: 'commercial', general: 'general', business: 'general',
      };
      const contactTypeNorm = contactTypeMap[(data.contact.contactType ?? '').toLowerCase().trim()] ?? (data.contact.contactType ?? '').toLowerCase().trim();
      const payload = {
        name: data.company.name, shortName: data.company.shortName, edrpou: data.company.edrpou,
        taxId: data.company.taxId || undefined, country: data.company.country,
        legalProfile: {
          fullLegalName: data.legal.fullLegalName, legalAddress: data.legal.legalAddress,
          actualAddress: data.legal.actualAddress || undefined, registrationCountry: data.legal.registrationCountry,
          vatStatus: vatStatusBool, vatNumber: data.legal.vatNumber || undefined,
          directorName: data.legal.directorName, authorizedPerson: data.legal.authorizedPerson || undefined,
          basisOfAuthority: data.legal.basisOfAuthority || undefined,
        },
        contacts: [{ fullName: data.contact.fullName, position: data.contact.position, email: data.contact.email, phone: data.contact.phone, contactType: contactTypeNorm, isPrimary: data.contact.isPrimary === 'true' }],
        bankAccounts: [{ bankName: data.bank.bankName, iban: data.bank.iban, currency: data.bank.currency, mfo: data.bank.mfo, accountHolder: data.bank.accountHolder, isPrimary: data.bank.isPrimary === 'true' }],
        roles: data.roles,
      };
      const result = await api.post<{ id: string }>('/onboarding/counterparty', payload);
      setSubmittedId(result.id);
      addToast(t('ob_success_title'), 'success');
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to create counterparty');
      addToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async (redirectTo?: string) => {
    if (!submittedId) return;
    setCompleting(true);
    try {
      await api.patch(`/onboarding/counterparty/${submittedId}/complete`, {});
      addToast(t('ob_complete'), 'success');
      navigate(redirectTo ?? '/');
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to complete onboarding');
      addToast(message, 'error');
    } finally {
      setCompleting(false);
    }
  };

  if (submittedId) {
    return (
      <DashboardLayout breadcrumb={[t('ob_title')]}>
        <div className="max-w-2xl mx-auto mt-12">
          <div className="rounded-xl p-10 text-center" style={{ background: successBg, border: `1px solid ${successBorder}` }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: successIconBg }}>
              <i className="ri-check-line text-3xl" style={{ color: '#0D7A3C' }}></i>
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
              {t('ob_success_title')}
            </h2>
            <p className="text-sm mb-6" style={{ color: textMuted }}>
              {t('ob_success_text')} <strong>{submittedId}</strong>
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => handleComplete('/')}
                disabled={completing}
                className="px-6 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                style={{ background: '#D12229' }}
              >
                {completing ? (
                  <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin"></i> {t('ob_completing')}</span>
                ) : t('ob_complete')}
              </button>
              <button
                onClick={() => handleComplete(`/counterparties/${submittedId}`)}
                disabled={completing}
                className="px-6 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                style={{ border: `1px solid ${outlineBtnBorder}`, color: outlineBtnColor }}
              >
                {t('ob_go_to_profile')}
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout breadcrumb={[t('ob_title')]}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-2">
          <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
            {t('ob_title')}
          </h1>
          <p className="text-sm mt-1" style={{ color: textMuted }}>
            {t('ob_subtitle')}
          </p>
        </div>

        <Stepper currentStep={step} totalSteps={5} labels={STEP_LABELS} />

        <div className="rounded-xl p-6 md:p-8" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          {step === 1 && <StepCompanyIdentity data={data.company} onChange={(d) => updateStepData('company', d)} errors={errors} />}
          {step === 2 && <StepLegalProfile data={data.legal} onChange={(d) => updateStepData('legal', d)} errors={errors} />}
          {step === 3 && <StepPrimaryContact data={data.contact} onChange={(d) => updateStepData('contact', d)} errors={errors} />}
          {step === 4 && <StepBankAccount data={data.bank} onChange={(d) => updateStepData('bank', d)} errors={errors} />}
          {step === 5 && <StepRoles selected={data.roles} onChange={(r) => updateStepData('roles', r)} errors={errors} />}

          <div className="flex items-center justify-between mt-8 pt-6" style={{ borderTop: `1px solid ${separatorBorder}` }}>
            <button
              onClick={handleBack}
              disabled={step === 1}
              className="px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              style={{ border: `1px solid ${backBtnBorder}`, color: backBtnColor }}
            >
              <i className="ri-arrow-left-line mr-1"></i> {t('ob_back')}
            </button>

            {step < 5 ? (
              <button
                onClick={handleNext}
                className="px-5 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors whitespace-nowrap"
                style={{ background: '#D12229' }}
              >
                {t('ob_next')} <i className="ri-arrow-right-line ml-1"></i>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-5 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                style={{ background: '#D12229' }}
              >
                {submitting ? (
                  <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin"></i> {t('ob_submitting')}</span>
                ) : (
                  <span>{t('ob_submit')} <i className="ri-send-plane-line ml-1"></i></span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}